import React, { useMemo } from 'react';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { Avatar } from '@components/basics/Images/Avatar';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { FeedCuratorTooltip } from '@components/main-components/tooltip/FeedCuratorTooltip';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import {
  FeedActivityItemUserInfoFragmentDoc,
  type FragmentType,
  UserActivityDetailFragmentDoc,
} from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { getActivityTimestamp } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

export const TopPostHeader = ({
  userFrag,
  timestamp,
  className,
}: {
  userFrag: FragmentType<FeedActivityItemUserInfoFragmentDoc>;
  timestamp: number;
  className?: string;
}) => {
  const feedUser = getFragment(FeedActivityItemUserInfoFragmentDoc, userFrag);
  const user = getFragment(UserActivityDetailFragmentDoc, feedUser);
  const { username, artist, avatar, publicAddress, webappUri } = user;
  const timestampText = getActivityTimestamp(timestamp);

  const userHoverCardContent = useMemo(() => <UserHoverCardContent userId={user.id} />, [user.id]);
  return (
    <div
      className={twMerge('flex flex-00auto items-center gap-2 md:gap-4', className)}
      data-feed-clickable-div
    >
      <UserHoverCard
        trigger={
          <div>
            <Avatar
              src={avatar?.url}
              username={username}
              size={40}
              borderRadius={999}
              walletAddress={publicAddress}
              hasBorder
              webappUri={webappUri}
            />
          </div>
        }
        userHoverCardContent={userHoverCardContent}
      />
      <div className="flex flex-col gap-1 ">
        <div className="self-start">
          <UserHoverCard
            trigger={
              <Link href={webappUriPath(webappUri)}>
                <div className="flex gap-1">
                  <div className="font-title text-[13px] font-medium leading-[15.6px] text-base600">
                    {username}
                  </div>
                  {!!artist && <VerifiedTooltip portal size={14} />}
                  <FeedCuratorTooltip portal size={12} />
                </div>
              </Link>
            }
            userHoverCardContent={userHoverCardContent}
          />
        </div>
        <div className="flex h-[11px] items-center self-start" data-feed-clickable-div>
          <DateTimeTooltip dateTime={new Date(timestamp)}>
            <span className="font-base text-[11px] font-medium leading-[11px]  text-base500">
              {timestampText}
            </span>
          </DateTimeTooltip>
        </div>
      </div>
      <div className="flex-1" data-feed-clickable-div />
    </div>
  );
};
